// The client documents vault (api/app/routers/documents.py, catalog in api/app/doc_catalog.json).
import { api } from './api';

export interface CatalogDoc {
  key: string;
  doc_type: string;
  name_en: string; name_ar: string;
  issuing_authority_en: string; issuing_authority_ar: string;
  proves_en: string; proves_ar: string;
  fields: string[];
  number_label_en?: string; number_label_ar?: string;
  expires: boolean;
  validity_note_en?: string;
  source_url: string; checked_date: string; verified: boolean;
  note_en?: string;
}
export interface CatalogAuthority { key: string; label_en: string; label_ar: string; emirate?: string | null; documents: CatalogDoc[] }
export interface CatalogCategory { key: string; label_en: string; label_ar: string; authorities: CatalogAuthority[] }
export interface Catalog {
  version: number; checked: string;
  categories: CatalogCategory[];
  doc_types: Record<string, { en: string; ar: string }>;
  emirates: Record<string, { en: string; ar: string }>;
  regulators: string[]; platforms: string[]; kaf_types: string[];
  max_bytes: number; content_types: string[];
}

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'skip';
export interface Check { key: string; label: string; status: CheckStatus; detail: string }
export interface Validation {
  overall?: CheckStatus;
  checks?: Check[];
  extracted?: { dates?: string[]; expiry?: string | null; issue?: string | null; numbers?: string[]; prefilled_expiry?: string | null };
  words?: number; confidence?: number | null;
}

export interface Doc {
  id: string; client_id: string;
  doc_type: string; type_label: string; type_label_ar: string;
  catalog_key?: string | null; name_en?: string | null; name_ar?: string | null;
  authority?: string | null; authority_en?: string | null; authority_ar?: string | null;
  verified: boolean; hint: string;
  title: string; number?: string | null; issue_date?: string | null; expiry_date?: string | null; emirate?: string | null;
  filename: string; content_type: string; size_bytes: number; notes?: string | null;
  uploaded_by_name?: string | null; uploaded_at: string; from_client: boolean;
  expiry_status: 'valid' | 'expiring' | 'expired' | 'no_expiry'; days_left?: number | null;
  ocr_status: 'pending' | 'running' | 'done' | 'failed' | 'hidden';
  validation: Validation;
  checked_by_name?: string | null; checked_at?: string | null; check_note?: string | null;
  history: Doc[];
}

export type SlotStatus = 'missing' | 'checking' | 'checked' | 'warnings' | 'mismatch' | 'expiring' | 'expired';
export interface Slot {
  key: string; doc_type: string; authority?: string | null;
  label_en: string; label_ar: string;
  suggest?: string | null; suggest_name_en?: string | null; suggest_name_ar?: string | null;
  verified: boolean; note_en: string;
  status: SlotStatus; document_id?: string | null; days_left?: number | null;
}
export interface Profile {
  emirate: string; regulator: string; free_zone: boolean; ad_platforms: string[];
  legal_name_en: string; legal_name_ar: string; inferred: string[];
}
export interface Vault {
  client_id: string; client_name: string; profile: Profile;
  checklist: Slot[]; documents: Doc[]; can_write: boolean; can_manage: boolean;
}
export interface PortalVault { client_name: string; needed: Slot[]; documents: Doc[] }

export interface RegisterIn {
  path: string; filename: string; doc_type: string; catalog_key?: string | null; authority?: string | null;
  title?: string | null; number?: string | null; issue_date?: string | null; expiry_date?: string | null;
  emirate?: string | null; notes?: string | null; replaces?: string | null;
}

export const fetchCatalog = async () => (await api.get<Catalog>('/documents/catalog')).data;
export const fetchVault = async (clientId: string) => (await api.get<Vault>(`/clients/${clientId}/documents`)).data;
export const saveProfile = async (clientId: string, p: Omit<Profile, 'inferred'>) =>
  (await api.put<Vault>(`/clients/${clientId}/documents/profile`, p)).data;
export const patchDoc = async (clientId: string, id: string, body: Record<string, unknown>) =>
  (await api.patch<Doc>(`/clients/${clientId}/documents/${id}`, body)).data;
export const recheckDoc = async (clientId: string, id: string) => (await api.post<Doc>(`/clients/${clientId}/documents/${id}/recheck`)).data;
export const markChecked = async (clientId: string, id: string, note?: string) =>
  (await api.post<Doc>(`/clients/${clientId}/documents/${id}/checked`, { note })).data;
export const deleteDoc = async (clientId: string, id: string) => { await api.delete(`/clients/${clientId}/documents/${id}`); };
export const docText = async (clientId: string, id: string) =>
  (await api.get<{ text: string; ocr_status: string; confidence: number | null }>(`/clients/${clientId}/documents/${id}/text`)).data;
export const fetchPortalVault = async () => (await api.get<PortalVault>('/portal/documents')).data;

/** Where a document lives: the staff vault of one clinic, or the clinic's own portal. */
export type Surface = { kind: 'staff'; clientId: string } | { kind: 'portal' };
const base = (s: Surface) => (s.kind === 'staff' ? `/clients/${s.clientId}/documents` : '/portal/documents');

/** A short signed link, minted per open, then followed: the file saves the browser's way. */
export async function openDoc(s: Surface, id: string) {
  const { data } = await api.get<{ url: string }>(`${base(s)}/${id}/download`);
  window.location.assign(data.url);
}

function put(url: string, file: File, type: string, onProgress?: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', type);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 95)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

export const DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.docx';
export function docContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ({ pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' } as Record<string, string>)[ext ?? ''] ?? '';
}

/** Signed PUT straight to the bucket, then register; the check runs on the server afterwards. */
export async function uploadDoc(s: Surface, file: File, fields: Omit<RegisterIn, 'path' | 'filename'>, onProgress?: (pct: number) => void): Promise<Doc> {
  const type = docContentType(file);
  const { data: u } = await api.post<{ upload_url: string; path: string; content_type: string }>(`${base(s)}/upload-url`,
    { filename: file.name, content_type: type, size_bytes: file.size });
  await put(u.upload_url, file, u.content_type, onProgress);
  const { data } = await api.post<Doc>(base(s), { ...fields, path: u.path, filename: file.name });
  onProgress?.(100);
  return data;
}

export const errText = (e: unknown, fallback = 'Something went wrong') => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: { msg?: string }) => x.msg).join('; ');
  return typeof d === 'string' ? d : (e as Error)?.message || fallback;
};
